/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { CSSTransition } from 'react-transition-group';
import { ToastNotification } from 'carbon-addons-iot-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import ReactHoverObserver from 'react-hover-observer';
import { injectIntl, defineMessages } from 'react-intl';

import { useTimeout } from 'customHooks';
import { formatTimestampMS } from 'formatterFunctions';

const messages = defineMessages({ closeError: 'Close error' });

const propTypes = {
  error: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    timestamp: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func,
  timeout: PropTypes.number,
  isHovering: PropTypes.bool,
  className: PropTypes.string,
};

const defaultProps = {
  timeout: 5000,
  isHovering: false,
  className: null,
  onClose: null,
};

/**
 * Toast based notification that renders for app-wide error notifications
 */
const ErrorNotification = ({
  error: { title, subtitle, id, timestamp },
  error,
  className,
  timeout,
  intl: { formatMessage },
  isHovering, // eslint-disable-line no-unused-vars
  onClose,
  ...other
}) => {
  const [showing, setShowing] = useState(true);

  // Only close if we're not hovering
  const handleCloseError = () => {
    if (!isHovering) {
      setShowing(false);
      if (onClose) {
        onClose(error);
      }
    }
  };

  const closeHover = useTimeout(handleCloseError, timeout);

  // automatically close the hover after timeout
  useEffect(
    () => {
      closeHover();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <CSSTransition
      key={`transition-${id}`}
      appear
      in={showing}
      timeout={timeout}
      classNames="error-notification">
      <ToastNotification
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...other}
        className={classNames('error-notification', className)}
        onCloseButtonClick={handleCloseError}
        title={title}
        subtitle={subtitle}
        iconDescription={formatMessage(messages.closeError)}
        caption={`Timestamp: ${formatTimestampMS(timestamp)}`}
        key={`errornotification-${id}`}
      />
    </CSSTransition>
  );
};

ErrorNotification.propTypes = propTypes;
ErrorNotification.defaultProps = defaultProps;
const ErrorNotificationWithIntl = injectIntl(ErrorNotification);

const ErrorNotificationWithHover = ({ ...props }) => (
  <ReactHoverObserver>
    <ErrorNotificationWithIntl // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  </ReactHoverObserver>
);

export default ErrorNotificationWithHover;
